import { useEffect, useState } from "react"
import { useMaterialUIController } from "context"

// @mui material components
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"
import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Collapse from "@mui/material/Collapse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"

// Material Dashboard 2 React examples
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// Tipos de asistente page components
import TiposAsistenteInformation from "layouts/asistencia/tipos-asistente/components/TiposAsistenteInformation"

// React Router
import { Link, useNavigate } from "react-router-dom"

// Config and JWT
import { jwtDecode } from "jwt-decode"

// Services
import { fetchTiposAsistente, fetchSucursales, fetchDependencias } from "layouts/asistencia/tipos-asistente/components/add/service"

function TiposAsistente() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const navigate = useNavigate()

  // Data states
  const [tiposAsistente, setTiposAsistente] = useState([])
  const [sucursales, setSucursales] = useState([])
  const [dependencias, setDependencias] = useState([])

  // Loading and UI
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(true)

  // Filters
  const [selectedSucursal, setSelectedSucursal] = useState("")
  const [selectedOrganizacion, setSelectedOrganizacion] = useState("")
  const [busquedaText, setBusquedaText] = useState("")

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadSucursales()
    loadTiposAsistente()
  }, [])

  // Load dependencias when sucursal changes
  useEffect(() => {
    if (selectedSucursal) {
      loadDependencias()
    } else {
      setDependencias([])
      setSelectedOrganizacion("")
    }
  }, [selectedSucursal])

  const loadSucursales = async () => {
    try {
      const data = await fetchSucursales()
      setSucursales(data || [])
    } catch (error) {
      console.error("Error loading sucursales:", error)
    }
  }

  const loadDependencias = async () => {
    try {
      const data = await fetchDependencias({ sucursalId: selectedSucursal })
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading dependencias:", error)
    }
  }

  const loadTiposAsistente = async () => {
    setLoading(true)
    try {
      const params = {}

      if (selectedOrganizacion) {
        params.dependenciaId = selectedOrganizacion
      }

      if (busquedaText) {
        params.nombre = busquedaText
      }

      const result = await fetchTiposAsistente(params)

      if (Array.isArray(result)) {
        setTiposAsistente(result)
      } else {
        setTiposAsistente([])
      }
    } catch (error) {
      console.error("Error loading tipos de asistente:", error)
      setTiposAsistente([])
    } finally {
      setLoading(false)
    }
  }

  const reloadTiposAsistente = () => {
    loadTiposAsistente()
  }

  const handleBuscar = () => {
    loadTiposAsistente()
  }

  const handleClearFilters = () => {
    setSelectedSucursal("")
    setSelectedOrganizacion("")
    setBusquedaText("")
    setTiposAsistente([])
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleBuscar()
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={8}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              {/* Filtros */}
              <Card>
                <MDBox px={2}>
                  <MDBox display="flex" alignItems="center" py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                      Filtros
                    </MDTypography>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      onClick={handleExpandClick}
                      sx={{ cursor: "pointer", ml: 1 }}
                    >
                      {expanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                    </MDBox>
                  </MDBox>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} pb={2}>
                      {/* Sucursal */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Sucursal</InputLabel>
                          <Select
                            label="Sucursal"
                            variant="outlined"
                            fullWidth
                            value={selectedSucursal}
                            onChange={(e) => setSelectedSucursal(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {sucursales.map((suc) => (
                              <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                                {suc.descripcion}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Dependencia */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Dependencia</InputLabel>
                          <Select
                            label="Dependencia"
                            variant="outlined"
                            fullWidth
                            value={selectedOrganizacion}
                            onChange={(e) => setSelectedOrganizacion(e.target.value)}
                            disabled={!selectedSucursal}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {dependencias.map((org) => (
                              <MenuItem key={org.id} value={org.id}>
                                {org.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Búsqueda */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Buscar por nombre"
                          variant="outlined"
                          fullWidth
                          value={busquedaText}
                          onChange={(e) => setBusquedaText(e.target.value)}
                          onKeyDown={handleKeyDown}
                        />
                      </Grid>

                      {/* Botones */}
                      <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <MDBox>
                            <MDButton type="submit" variant="gradient" color="info" onClick={handleBuscar}>
                              Buscar
                            </MDButton>
                            &nbsp;
                            <MDButton type="button" variant="outlined" color="info" onClick={handleClearFilters}>
                              Limpiar
                            </MDButton>
                          </MDBox>

                          <Link to="/asistencia/tipos-asistente/agregar" style={{ textDecoration: "none" }}>
                            <MDButton type="button" variant="gradient" color="success">
                              Agregar
                            </MDButton>
                          </Link>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                </MDBox>
              </Card>

              <Divider />

              {/* Tabla de Tipos de Asistente */}
              <Card pt={5} mt={3}>
                {/* Contenido */}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : tiposAsistente && tiposAsistente.length > 0 ? (
                  <TiposAsistenteInformation tiposAsistente={tiposAsistente} onReload={reloadTiposAsistente} />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="h6" color="text">
                      No hay datos para mostrar
                    </MDTypography>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default TiposAsistente
